import { FC } from 'react';
import {
  ActionsMenu,
  Table,
  TableToolbar,
  TableWrapper,
  Tabs,
  Tile,
  Typography,
} from 'melp-design/components';
import { Divider, Skeleton, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'utils/navigation';
import {
  transmissionActsFiltersSchema,
  useDeposits,
  useDownloadTransmissionActPdfOnServer,
  useExportToXls,
  useTransactions,
  useTransmissionActs,
} from 'store/transmission-acts';
import { formatCurrency } from 'utils/format';
import { APP } from 'config';
import { useDefaultCurrency, useMe } from 'state/Administrators';
import {
  CompaniesFilter,
  DateRangeFilter,
  FilterButton,
  Filters,
} from 'components/filters';
import moment from 'moment';
import { DownloadXlsAction } from 'components/actions';

export const TransmissionActsList: FC = () => {
  const { t, i18n } = useTranslation();
  const { currency } = useDefaultCurrency();
  const { me } = useMe();

  const { searchParams: allFilters, navigate } = useSearchParams(
    transmissionActsFiltersSchema,
  );
  const { listVariant, ...filters } = allFilters;

  const { data: transmissionActs, isLoading: isTransmissionActsLoading } =
    useTransmissionActs(filters, { enabled: listVariant === 'reports' });
  const { data: transactions, isLoading: isTransactionsLoading } =
    useTransactions(filters, { enabled: listVariant === 'balance' });
  const { data: deposits, isLoading: isDepositsLoading } = useDeposits(
    filters,
    { enabled: listVariant === 'deposits' },
  );

  const { mutate: exportToXls, isLoading: isExportingToXls } = useExportToXls();
  const { mutate: downloadPdf, isLoading: isDownloading } =
    useDownloadTransmissionActPdfOnServer();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <Tabs
              value={listVariant}
              onChange={(v) => navigate({ params: { listVariant: v } })}
              items={[
                {
                  value: 'reports',
                  label: t('alternatives.reports.title'),
                },
                {
                  value: 'balance',
                  label: t('transmissionActs.balance.label'),
                },
                {
                  value: 'deposits',
                  label: t('expenses.filters.type.options.deposit'),
                },
              ]}
            />

            <Stack direction="row" alignItems="center" gap={1}>
              <Filters
                value={filters}
                initialValues={transmissionActsFiltersSchema.parse({})}
                onChange={(v) => navigate({ params: { ...v, page: 1 } })}
              >
                {({ value, initialValues, setFilter }) => (
                  <>
                    <FilterButton
                      label={t('common.date')}
                      value={value.date}
                      initialValue={initialValues.date}
                      onChange={(v) => setFilter('date', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <DateRangeFilter
                          value={value}
                          onApply={applyFilter}
                          onClear={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    {me?.parentCompany?.settings.depositManagementMethod ===
                    'company' ? (
                      <FilterButton
                        label={t('employees.company')}
                        value={value.company}
                        initialValue={initialValues.company}
                        onChange={(v) => setFilter('company', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <CompaniesFilter
                            value={value}
                            onApplyFilter={applyFilter}
                            onClearFilter={clearFilter}
                            multiple={false}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>
                    ) : null}
                  </>
                )}
              </Filters>

              {listVariant === 'balance' ? (
                <>
                  <Divider orientation="vertical" variant="middle" flexItem />

                  <DownloadXlsAction
                    listLength={transactions?.total ?? 0}
                    exportToExcel={() =>
                      exportToXls({
                        params: {
                          ongoingBalance: true,
                          companyId:
                            me?.parentCompany?.settings
                              .depositManagementMethod === 'company'
                              ? filters.company
                              : undefined,
                        },
                      })
                    }
                    loading={isExportingToXls}
                  />
                </>
              ) : null}
            </Stack>
          </Stack>

          {listVariant === 'balance' && filters.company ? (
            <>
              <Divider />

              <Stack direction="row" alignItems="center" gap={2}>
                {isTransactionsLoading ? (
                  <>
                    <Skeleton variant="rounded" width={200} height={81} />
                    <Skeleton variant="rounded" width={200} height={81} />
                    <Skeleton variant="rounded" width={200} height={81} />
                  </>
                ) : transactions ? (
                  <>
                    <Tile
                      title={t('transmissionActs.openingBalance.label')}
                      items={[
                        formatCurrency(
                          i18n.language,
                          transactions.meta.openingBalance,
                          currency,
                        ),
                      ]}
                    />
                    <Tile
                      title={t('transmissionActs.closingBalance.label')}
                      items={[
                        formatCurrency(
                          i18n.language,
                          transactions.meta.closingBalance,
                          currency,
                        ),
                      ]}
                    />
                  </>
                ) : null}
              </Stack>
            </>
          ) : null}
        </Stack>
      </TableToolbar>

      {listVariant === 'reports' ? (
        <Table
          data={transmissionActs?.items ?? []}
          isLoading={isTransmissionActsLoading}
          columns={[
            {
              id: 'id',
              header: t('integrations.hiBobServiceUserId'),
              cell: ({ row: { original: transmissionAct } }) => {
                return transmissionAct.sequence || transmissionAct.id;
              },
            },
            {
              id: 'date',
              header: t('common.date'),
              cell: ({ row: { original: transmissionAct } }) => {
                return moment(transmissionAct.from).format('MM YYYY');
              },
              meta: { sort: true },
            },
            {
              id: 'company',
              header: t('employees.company'),
              cell: ({ row: { original: transmissionAct } }) => {
                return transmissionAct.company?.name ?? null;
              },
              meta: { sort: true },
            },
            {
              id: 'openingBalance',
              header: t('transmissionActs.openingBalance.label'),
              cell: ({ row: { original: transmissionAct } }) => {
                return (
                  <Typography
                    color={
                      transmissionAct.openingBalance < 0
                        ? 'error'
                        : 'textPrimary'
                    }
                  >
                    {formatCurrency(
                      i18n.language,
                      transmissionAct.openingBalance,
                      currency,
                    )}
                  </Typography>
                );
              },
              meta: { align: 'right' },
            },
            {
              id: 'closingBalance',
              header: t('transmissionActs.closingBalance.label'),
              cell: ({ row: { original: transmissionAct } }) => {
                return (
                  <Typography
                    color={
                      transmissionAct.closingBalance < 0
                        ? 'error'
                        : 'textPrimary'
                    }
                  >
                    {formatCurrency(
                      i18n.language,
                      transmissionAct.closingBalance,
                      currency,
                    )}
                  </Typography>
                );
              },
              meta: { align: 'right' },
            },
            {
              id: 'actions',
              cell: ({ row: { original: transmissionAct } }) => {
                return (
                  <ActionsMenu
                    type="compact"
                    items={[
                      {
                        label: t('dashboard.download_xls'),
                        onClick: () => {
                          exportToXls({
                            params: { transmissionActId: transmissionAct.id },
                          });
                        },
                        disabled: isExportingToXls,
                      },
                      {
                        label: t('common.downloadPDF'),
                        onClick: () => {
                          downloadPdf({
                            transmissionActId: transmissionAct.id,
                          });
                        },
                        disabled: isDownloading,
                      },
                    ]}
                  />
                );
              },
            },
          ]}
          pagination={
            transmissionActs && {
              count: transmissionActs.total,
              page: filters.page,
              pageSize: filters.pageSize,
              pageSizeOptions: APP.pagination.defaults.pageSize,
              onChange: (page, pageSize) => {
                navigate({ params: { page, pageSize } });
              },
            }
          }
          sort={{
            value: filters.sort,
            onChange: (v) => navigate({ params: { sort: v } }),
          }}
          rows={{
            inactive: ({ original: transmissionAct }) =>
              transmissionAct.company?.status === 'inactive',
          }}
        />
      ) : listVariant === 'balance' ? (
        <Table
          data={transactions?.items ?? []}
          isLoading={isTransactionsLoading}
          columns={[
            {
              id: 'date',
              header: t('common.date'),
              cell: ({ row: { original: transaction } }) => {
                return transaction.type === 'deposit'
                  ? moment(transaction.date).format('l')
                  : null;
              },
            },
            {
              id: 'source',
              header: t('wallet.employeeWalletFundsSource'),
              cell: ({ row: { original: transaction } }) => {
                return transaction.type === 'expense'
                  ? `${transaction.title} (${transaction.count})`
                  : transaction.title;
              },
            },
            {
              id: 'amount',
              header: t('employees.amount'),
              cell: ({ row: { original: transaction } }) => {
                return formatCurrency(
                  i18n.language,
                  transaction.amount,
                  transaction.currency,
                );
              },
            },
            {
              id: 'balance',
              header: t('transmissionActs.balance.label'),
              cell: ({ row: { original: transaction } }) => {
                return formatCurrency(
                  i18n.language,
                  transaction.balance,
                  transaction.currency,
                );
              },
            },
          ]}
        />
      ) : listVariant === 'deposits' ? (
        <Table
          data={deposits?.items ?? []}
          isLoading={isDepositsLoading}
          columns={[
            {
              id: 'creationDate',
              header: t('common.date'),
              cell: ({ row: { original: deposit } }) => {
                return moment(deposit.date).format('l');
              },
              meta: { sort: true },
            },
            {
              id: 'amount',
              header: t('employees.amount'),
              cell: ({ row: { original: deposit } }) => {
                return formatCurrency(
                  i18n.language,
                  deposit.amount,
                  deposit.currency,
                );
              },
              meta: { sort: true, align: 'right' },
            },
            {
              id: 'accountNumber',
              header: t('common.invoice_number.label'),
              cell: ({ row: { original: deposit } }) => {
                return deposit.accountNumber;
              },
              meta: { sort: true },
            },
          ]}
          pagination={
            deposits && {
              count: deposits.total,
              page: filters.page,
              pageSize: filters.pageSize,
              pageSizeOptions: APP.pagination.defaults.pageSize,
              onChange: (page, pageSize) => {
                navigate({ params: { page, pageSize } });
              },
            }
          }
          sort={{
            value: filters.sort,
            onChange: (v) => navigate({ params: { sort: v } }),
          }}
        />
      ) : null}
    </TableWrapper>
  );
};
